import { startCase, upperCase } from 'lodash';
import moment from 'moment';

import getLeftPart from 'js/lib/getLeftPart';

import { ALGOLIA_ENTERPRISE_INDEX } from 'bundles/enterprise-learner-search/constants';

type AlgoliaRequest = {
  indexName: string;
  params: {
    facetFilters?: string[][];
    facets: string[];
    filters: string;
    highlightPostTag: string;
    highlightPreTag: string;
    hitsPerPage: number;
    optionalFilters: string;
    page: number;
    query: string;
    tagFilters: string;
  };
};

type searchHits = {
  searchProductHit: {
    name?: string;
    id?: string;
    partners?: string[];
    imageUrl?: string;
    productType?: string;
    avgProductRating?: number;
    productDifficultyLevel?: string;
    partnerLogoUrls?: string[];
    enrollments?: string;
    numProductRatings?: string;
    url?: string;
    duration?: string;
  };
};

type searchResults = {
  facets: { name: string; valuesAndCounts: { value: string; count: number }[] }[];
  hits: searchHits[];
  numberOfHits: string;
  nextPage: number;
  page: number;
  source: { engine: string; indexName: string };
  totalPages: string;
};

type SearchAPIData = {
  searchResults?: searchResults[];
};

const mapFacetsBack = (facet: string) =>
  facet.replace('productDuration', 'productDurationEnum').replace('productTypeDescription', 'entityTypeDescription');

// convert to ISO8601 duration
const protobufSecondsToDuration = (duration: string) => `PT${duration?.replace('s', 'S')}`;

export const mapDataToAlgoliaFormat = (algoliaRequests: AlgoliaRequest[]) => (data: SearchAPIData) => {
  // need to map the returned values to algolia format so it works with their components
  const algoliaData = {
    results: data?.searchResults?.map((result, index: number) => {
      const facets: Record<string, Record<string, number>> = {};
      result.facets.forEach(({ name, valuesAndCounts }) => {
        const facetName = mapFacetsBack(name);
        facets[facetName] = {};
        valuesAndCounts?.forEach(({ value, count }) => {
          facets[facetName][value] = count;
        });
      });

      return {
        ...result,
        hits: result.hits.map(({ searchProductHit: hit }) => ({
          ...hit,
          objectID: hit?.id || '',
          name: hit?.name || '',
          partnerLogos: hit.partnerLogoUrls || [],
          partners: hit.partners || [],
          imageUrl: hit.imageUrl || '',
          entityType: upperCase(hit?.productType?.replace('PRODUCT_TYPE_', '') || ''),
          productDifficultyLevel: startCase(
            hit.productDifficultyLevel?.replace('PRODUCT_DIFFICULTY_LEVEL_', '')?.toLowerCase() || ''
          ),
          numProductRatings: Number(hit?.numProductRatings || '0'),
          avgProductRating: hit.avgProductRating || 0,
          enrollments: Number(hit?.enrollments || '0'),
          durationMs: hit?.duration && moment.duration(protobufSecondsToDuration(hit?.duration)).asMilliseconds(),
          relatedS12nName: '',
          courseOrderInSpecialization: 0,
          subtitleLanguage: [],
          language: '',
          primaryDomain: '',
          primarySubdomain: '',
          customerCompetencies: [],
          entityTypeDescription: '',
          skills: [],
          isPrivate: false,
          tagline: '',
          avgLearningHours: 0,
          description: '',
          courseOrS12nId: '',
          fullObjectUrl: '',
          objectUrl: hit.url,
        })),
        facets,
        hitsPerPage: 10,
        nbHits: Number(result.numberOfHits),
        nbPages: Number(result.totalPages),
        index: algoliaRequests[index].indexName,
        query: algoliaRequests[index].params.query,
      };
    }),
  };
  return algoliaData;
};

const SFCFilters = [
  {
    facetFilter: ['productTypeDescription:-Short Form Content Video'],
  },
  {
    facetFilter: ['productTypeDescription:-Short Form Content Lesson'],
  },
];

const mapFacetName = (facet: string) =>
  facet?.replace('productDurationEnum', 'productDuration')?.replace('entityTypeDescription', 'productTypeDescription');

const mapFacetFilters = (
  facetFilters: (string[] | undefined)[] | undefined | null,
  shouldEnableShortFormContent: boolean
) => [
  ...(shouldEnableShortFormContent ? [] : SFCFilters),
  ...(facetFilters
    ?.filter((filters) => (filters?.length || 0) > 0)
    ?.map((filters: string[] | undefined) => ({ facetFilter: filters })) || []),
];

const mapFacets = (facets: string[] | string | undefined | null) => {
  if (typeof facets === 'string') return [mapFacetName(facets)];
  return facets?.map((facet) => mapFacetName(facet));
};

/** @deprecated please please please do NOT use, replaced by the new SearchProvider */
export const getCustomSearchClientBySearchInPrograms = (
  programIds: Array<string>,
  shouldEnableShortFormContent: boolean
): { search: (requests: unknown) => void } => {
  // cache adapted from https://github.com/algolia/react-instantsearch/issues/2847#issuecomment-535476270

  const cache = new Map();
  const promiseCache = new Map();

  const doRequest = (body: string, requests: AlgoliaRequest[]) =>
    fetch(`${getLeftPart()}/api/grpc/search/v1beta1/SearchAPI/Search`, {
      method: 'post',
      headers: {
        'Content-Type': 'application/json',
      },
      body,
    }).then((res) => res.json().then(mapDataToAlgoliaFormat(requests)));

  const customSearchClient = {
    async search(requests: unknown) {
      const algoliaRequests = requests as AlgoliaRequest[];
      const body = JSON.stringify({
        requests: algoliaRequests.map((request) => ({
          ...request,
          ...request.params,
          entityType: 'ENTITY_TYPE_PRODUCTS',
          context: {
            searchInPrograms: {
              programIds,
            },
          },
          facetFilters: mapFacetFilters(
            [
              ...(request.params?.facetFilters || []),
              request.params?.filters ? [request.params?.filters?.replaceAll("'", '')] : undefined,
            ],
            shouldEnableShortFormContent && request.indexName !== ALGOLIA_ENTERPRISE_INDEX
          ),
          filterAttributes: mapFacets(request.params?.facets),
          pagination: { limit: request.params?.hitsPerPage, page: request.params.page },
        })),
      });

      const cached = cache.get(body);
      if (cached) {
        return cached;
      }

      const promiseCached = promiseCache.get(body);
      if (promiseCached) {
        return promiseCached;
      }

      const promise = doRequest(body, algoliaRequests);
      promiseCache.set(body, promise);
      const response = await promise;
      cache.set(body, response);
      promiseCache.delete(body);
      return response;
    },
  };

  return customSearchClient;
};
